import { Result } from "../../../shared/api/services/result";
import { AuthApiService } from "../../auth/api/AuthApiService";
import { isMockEnabled, getMockClosureResponse } from "../../../shared/dev/mockData";
import { uploadErrorMessageFromText } from "../../../shared/utils/uploadErrors";

/**
 * Request body for closing an existing clinic (not registered through the system)
 */
export interface CloseExistingClinicRequest {
  name: string;
  governorate: string;
  area_id: string;
  neighborhood?: number;
  street?: number;
  house_number?: number;
  nearest_landmark?: string;
  latitude?: number;
  longitude?: number;
  phone_primary: string;
  phone_secondary?: string;
  syndicate_order_number?: string;
  opening_certificate_number?: string;
  closure_reason?: string;
  closure_date?: string; // YYYY-MM-DD
  temporary_closure_end_date?: string; // YYYY-MM-DD
}

/**
 * Request body for closing a registered clinic
 */
export interface CloseRegisteredClinicRequest {
  closure_reason?: string;
  closure_date?: string; // YYYY-MM-DD
}

/**
 * Response from closure request creation
 */
export interface ClosureRequestResponse {
  id: string;
  status: string;
  closure_reason?: string;
  closure_date?: string;
  created_at: string;
  clinic?: {
    id: string;
    name: string;
    status: string;
  };
}

/**
 * API Service for Clinic Closure workflow
 * Endpoint prefix: /clinic/doctor/closure/
 */
export class ClosureApiService {
  private baseURL: string;
  private authApi = AuthApiService.getInstance();

  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
  }

  /**
   * Create a closure request for an existing clinic NOT registered through the system
   * POST /clinic/doctor/closure/close-existing-clinic
   */
  async closeExistingClinic(data: CloseExistingClinicRequest): Promise<Result<ClosureRequestResponse>> {
    if (isMockEnabled()) {
      console.log("[Mock] closeExistingClinic", data);
      return Result.success(getMockClosureResponse() as ClosureRequestResponse);
    }

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/closure/close-existing-clinic`,
        {
          method: "POST",
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.detail || error.message || "Failed to create closure request"
        );
      }

      const result = await response.json();
      console.log("✅ [Closure] Created existing clinic closure request");
      return Result.success(result.data || result);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to create closure request"
      );
    }
  }

  /**
   * Create a closure request for a clinic registered through the system
   * POST /clinic/doctor/closure/close-registered-clinic
   */
  async closeRegisteredClinic(data: CloseRegisteredClinicRequest): Promise<Result<ClosureRequestResponse>> {
    if (isMockEnabled()) {
      console.log("[Mock] closeRegisteredClinic", data);
      return Result.success(getMockClosureResponse() as ClosureRequestResponse);
    }

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/closure/close-registered-clinic`,
        {
          method: "POST",
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.detail || error.message || "Failed to create closure request"
        );
      }

      const result = await response.json();
      console.log("✅ [Closure] Created registered clinic closure request");
      return Result.success(result.data || result);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to create closure request"
      );
    }
  }

  /**
   * Update a registered clinic closure request
   * PUT /clinic/doctor/closure/{request_id}/update-registered-closure
   */
  async updateRegisteredClosure(
    requestId: string,
    data: CloseRegisteredClinicRequest
  ): Promise<Result<ClosureRequestResponse>> {
    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/closure/${requestId}/update-registered-closure`,
        {
          method: "PUT",
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.detail || error.message || "Failed to update closure request"
        );
      }

      const result = await response.json();
      return Result.success(result.data || result);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to update closure request"
      );
    }
  }

  /**
   * Update an existing clinic closure request
   * PUT /clinic/doctor/closure/{request_id}/update-existing-closure
   */
  async updateExistingClosure(
    requestId: string,
    data: CloseExistingClinicRequest
  ): Promise<Result<ClosureRequestResponse>> {
    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/closure/${requestId}/update-existing-closure`,
        {
          method: "PUT",
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.detail || error.message || "Failed to update closure request"
        );
      }

      const result = await response.json();
      return Result.success(result.data || result);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to update closure request"
      );
    }
  }

  /**
   * Get all closure requests for the current doctor
   * GET /clinic/doctor/closure/
   */
  async getClosureRequests(): Promise<Result<ClosureRequestResponse[]>> {
    if (isMockEnabled()) {
      return Result.success([getMockClosureResponse() as ClosureRequestResponse]);
    }

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/closure/`
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.detail || error.message || "Failed to get closure requests"
        );
      }

      const result = await response.json();
      return Result.success(result.data || []);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to get closure requests"
      );
    }
  }

  /**
   * Get a single closure request with full attachment data
   * GET /clinic/doctor/closure/{id}/with-attachments
   */
  async getClosureWithAttachments(requestId: string): Promise<Result<any>> {
    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/closure/${requestId}/with-attachments`
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.detail || error.message || "Failed to get closure details"
        );
      }

      const result = await response.json();
      return Result.success(result.data || result);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to get closure details"
      );
    }
  }

  /**
   * Agree to terms and conditions for a closure request
   * POST /clinic/doctor/closure/{request_id}/agree-to-terms
   */
  async agreeToTerms(agreed: boolean, requestId: string): Promise<Result<{ message: string }>> {
    try {
      console.log("📋 [Closure agreeToTerms] Using request ID:", requestId);

      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/closure/${requestId}/agree-to-terms`,
        {
          method: "POST",
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message || error.detail || "Failed to agree to terms"
        );
      }

      const result = await response.json();

      if (result.status === false) {
        return Result.failure(
          result.data?.message || "Failed to agree to terms"
        );
      }

      // Trigger refresh because agreeing to terms might advance progress
      window.dispatchEvent(new CustomEvent("doctor-progress-refresh"));

      console.log("✅ [Closure] Terms agreed successfully");
      return Result.success(result.data || { message: "Terms agreed" });
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to agree to terms"
      );
    }
  }

  /**
   * Get closure request details by ID
   * GET /clinic/doctor/closure/{id}
   */
  async getClosureRequest(requestId: string): Promise<Result<ClosureRequestResponse>> {
    if (isMockEnabled()) {
      return Result.success(getMockClosureResponse() as ClosureRequestResponse);
    }

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/closure/${requestId}`
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.detail || error.message || "Failed to get closure request"
        );
      }

      const result = await response.json();
      return Result.success(result.data || result);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to get closure request"
      );
    }
  }

  /**
   * Cancel a closure request (only valid while the request is in pre-submit / draft state).
   * POST /clinic/doctor/closure/{request_id}/cancel
   */
  async cancelClosureRequest(requestId: string): Promise<Result<any>> {
    if (isMockEnabled()) {
      console.log("[Mock] cancelClosureRequest", requestId);
      return Result.success({ message: "Mock cancel successful" });
    }

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/closure/${requestId}/cancel`,
        { method: "POST" }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.detail || error.message || "Failed to cancel closure request"
        );
      }

      const result = await response.json();
      console.log("✅ [Closure] Cancelled closure request");
      return Result.success(result.data || result);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to cancel closure request"
      );
    }
  }

  /**
   * Upload attachment for a closure request
   * POST /clinic/doctor/closure/{id}/attachments/upload
   */
  async uploadAttachment(
    requestId: string,
    file: File,
    documentType: string,
    onProgress?: (progress: number) => void
  ): Promise<Result<any>> {
    const accessToken = this.authApi.getAccessToken();
    if (!accessToken) {
      return Result.failure("No authentication token available");
    }

    try {
      const url = `${this.baseURL}/clinic/doctor/closure/${requestId}/attachments/upload`;

      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();

        formData.append("file", file, file.name);
        formData.append("document_type", documentType);

        if (onProgress) {
          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) {
              const percentComplete = Math.round((e.loaded / e.total) * 100);
              onProgress(percentComplete);
            }
          });
        }

        xhr.onload = async () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const responseData = JSON.parse(xhr.responseText);
              console.log(`✅ [Closure] Uploaded ${documentType} successfully`);
              resolve(Result.success(responseData.data || responseData));
            } catch {
              resolve(Result.success({ message: "Upload successful" }));
            }
          } else if (xhr.status === 401 || xhr.status === 444) {
            const refreshResult = await this.authApi.refreshAccessToken();
            if (refreshResult.isSuccess) {
              resolve(this.uploadAttachment(requestId, file, documentType, onProgress));
            } else {
              resolve(Result.failure("Authentication failed"));
            }
          } else {
            console.error(`❌ [Closure] Upload failed with status ${xhr.status}:`, xhr.responseText);
            resolve(Result.failure(uploadErrorMessageFromText(xhr.status, xhr.responseText)));
          }
        };

        xhr.onerror = () => {
          console.error("❌ [Closure] Network error during upload");
          resolve(Result.failure("Network error during upload"));
        };

        xhr.ontimeout = () => {
          console.error("❌ [Closure] Upload timed out");
          resolve(Result.failure("Upload timed out"));
        };

        xhr.open("POST", url);
        xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
        xhr.timeout = 60000;

        console.log(`📤 [Closure] Uploading ${documentType}: ${file.name} (${file.size} bytes)`);
        xhr.send(formData);
      });
    } catch (error) {
      console.error("❌ [Closure] Exception during upload:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to upload attachment"
      );
    }
  }
}
